// Image insertion must answer ONLYOFFICE's onRequestInsertImage event. Uploading an
// image alone does not attach it to a document. Keep that request bound to its editor.
export function createSignatureFlow(upload) {
  let active = null;
  let request = null;
  let prepared = null;
  let busy = false;
  const clear = () => { active = null; request = null; prepared = null; busy = false; };
  const assertEditable = () => {
    if (!active?.ready || !active.editable || !active.editor) throw new Error('The current document is not ready for signing or is read-only.');
  };
  const apply = async () => {
    assertEditable();
    if (!request || !prepared) return 'needs-signature';
    if (busy) throw new Error('A signature is already being added.');
    const context = active;
    const insertion = request;
    const image = prepared;
    busy = true;
    try {
      const data = await upload(context.documentId, image, insertion.command);
      if (active !== context || request !== insertion || !active.editable) {
        throw new Error('The document changed while the signature was uploading. Please sign the current file again.');
      }
      if (!data?.imageUrl || !data?.fileType) throw new Error('The signature upload response is incomplete.');
      context.editor.insertImage({ c: insertion.command, images: [{ fileType: data.fileType, url: data.imageUrl }], token: data.token });
      request = null;
      prepared = null;
      return 'sent';
    } finally { if (active === context) busy = false; }
  };
  return {
    clear,
    activate(documentId, editor, editable) { clear(); active = { documentId, editor, editable, ready: false }; },
    ready(documentId) { if (String(active?.documentId) === String(documentId)) active.ready = true; },
    cancel() { request = null; prepared = null; },
    async request(documentId, command = 'add') {
      assertEditable();
      if (String(active.documentId) !== String(documentId)) throw new Error('This image request belongs to a different document.');
      if (!['add', 'change', 'fill', 'watermark', 'slide'].includes(command)) throw new Error('Unsupported image insertion request.');
      request = { command };
      return prepared ? apply() : 'needs-signature';
    },
    async submit(image) {
      assertEditable();
      prepared = image;
      return request ? apply() : 'needs-placement';
    },
  };
}
